import { useEffect, useState } from "react";
import { dashboardCardSx } from "../constants";
import Card from "@mui/material/Card";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";

function ChannelCell({ channel, type, hasAlert }) {
  {/* <span className={`dashboard-2-channel-value ${hasAlert ? "alert-value" : valueClass(type, channel.numericPercent)}`}>{channel.percent}</span> */ }

  if(hasAlert){

    let channelSame = channel?.channelNumber === hasAlert?.alert?.channel_number

    if(channelSame){
      const styling = {
        backgroundColor: hasAlert.badgePick.background_color_hex, 
        borderColor: hasAlert.badgePick.border_color_hex, 
        color: hasAlert.badgePick.font_color_hex,
        padding: '4px 8px',
        borderRadius: '6px'
      }
      return (
        <td className="dashboard-2-channel-cell">
          <span className={`dashboard-2-channel-value`} style={styling}>{channel.percent}</span>
          <span className="dashboard-2-channel-meta">{channel.volume}</span>
        </td>
      );
    }
  }

  return (
    <td className="dashboard-2-channel-cell">
      <span className={`dashboard-2-channel-value`}>{channel.percent}</span>
      <span className="dashboard-2-channel-meta">{channel.volume}</span>
    </td>
  );
}

function TempCell({ channel, hasAlert }) {
  return (
    <td className="dashboard-2-channel-cell">
      <span className={`dashboard-2-channel-value`}>{channel.value}</span>
    </td>
  );
  // return (
  //   <td className="dashboard-2-channel-cell">
  //     <span className={`dashboard-2-channel-value ${hasAlert ? "alert-active" : ""}`}>{channel.value}</span>
  //   </td>
  // );
}

function DeviceDashboardTable({ rows, sortState, onSort, onRowClick, onAlertClick }) {
  const sortMark = (key) => (sortState.key === key ? ` ${sortState.direction}` : "");

  // Define all possible columns with metadata
  const allColumns = [
    { key: "name", label: "Main Device", sortable: true, rowSpan: 2 },
    { key: "alert", label: "Alert", sortable: true, rowSpan: 2 },
    // Tank group header (colSpan 4) – handled separately
    { key: "fresh", label: "Fresh", sortable: true },
    { key: "waste", label: "Waste", sortable: true },
    { key: "waste2", label: "Waste 2", sortable: true },
    { key: "waste3", label: "Waste 3", sortable: true },
    // Temperature group header (colSpan 3)
    { key: "menTemp", label: "Men", sortable: true },
    { key: "womenTemp", label: "Women", sortable: true },
    { key: "utilityTemp", label: "Utility", sortable: true },
    { key: "batteryVoltage", label: "Battery", sortable: true, rowSpan: 2 },
    { key: "status", label: "Status", sortable: true, rowSpan: 2 },
    { key: "power", label: "Power", sortable: true, rowSpan: 2 },
    { key: "location", label: "Location / Entity", sortable: true, rowSpan: 2 },
    { key: "identifier", label: "Identifier", sortable: true, rowSpan: 2 },
    { key: "lastUpdated", label: "Last Updated", sortable: true, rowSpan: 2 },
  ];

  // State to keep current column order; initialise with saved order from localStorage if present
  const [columnOrder, setColumnOrder] = useState(() => {
    try {
      const saved = localStorage.getItem('dashboard2ColumnOrder');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Validate that parsed is an array of strings and contains at least one known key
        if (Array.isArray(parsed) && parsed.every((k) => typeof k === 'string')) {
          return parsed;
        }
      }
    } catch (e) {
      // ignore parsing errors and fall back to default
    }
    return allColumns.map((c) => c.key);
  });

  // Persist column order to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('dashboard2ColumnOrder', JSON.stringify(columnOrder));
    } catch (e) {
      // ignore storage errors (e.g., quota exceeded)
    }
  }, [columnOrder]);

  // Drag‑and‑drop handlers for column reordering
  const onDragStart = (e, draggedKey) => {
    e.dataTransfer.setData("text/plain", draggedKey);
    // Needed for Firefox
    e.dataTransfer.effectAllowed = "move";
  };

  const onDrop = (e, targetKey) => {
    e.preventDefault();
    const draggedKey = e.dataTransfer.getData("text/plain");
    if (!draggedKey || draggedKey === targetKey) return;
    const newOrder = [...columnOrder];
    const fromIdx = newOrder.indexOf(draggedKey);
    const toIdx = newOrder.indexOf(targetKey);
    newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, draggedKey);
    setColumnOrder(newOrder);
  };

  const onDragOver = (e) => {
    e.preventDefault();
  };

  // Helper to render a header cell based on column definition
  const renderHeaderCell = (col) => {
    const content = col.label + (col.sortable ? sortMark(col.key) : "");
    const commonProps = {
      key: col.key,
      draggable: true,
      onDragStart: (e) => onDragStart(e, col.key),
      onDrop: (e) => onDrop(e, col.key),
      onDragOver,
    };
    if (col.rowSpan) {
      return (
        <th rowSpan={col.rowSpan} {...commonProps} onClick={() => col.sortable && onSort(col.key)}>
          {content}
        </th>
      );
    }
    // For sub‑header cells (no rowSpan)
    return (
      <th {...commonProps} onClick={() => col.sortable && onSort(col.key)}>
        {content}
      </th>
    );
  };

  // Determine which columns belong to the Tank and Temperature groups
  const tankKeys = ["fresh", "waste", "waste2", "waste3"];
  const tempKeys = ["menTemp", "womenTemp", "utilityTemp"];

  // Build ordered column definitions based on current order
  const orderedCols = columnOrder.map((key) => allColumns.find((c) => c.key === key)).filter(Boolean);

  return (
    <Card sx={dashboardCardSx}>
      <MDBox sx={{ overflowX: "auto" }}>
        <table className="dashboard-2-table">
          <thead>
            <tr>
              {/* Columns that span two rows and should appear before group headers (Main Device, Alert) */}
              {orderedCols
                .filter((c) => c.rowSpan === 2 && ["name", "alert"].includes(c.key))
                .map((c) => renderHeaderCell(c))}
              {/* Tank group header */}
              <th colSpan={tankKeys.filter((k) => columnOrder.includes(k)).length}>Tank</th>
              {/* Temperature group header */}
              <th colSpan={tempKeys.filter((k) => columnOrder.includes(k)).length}>Temperature</th>
              {/* Columns that span two rows and appear after group headers */}
              {orderedCols
                .filter((c) => c.rowSpan === 2 && !["name", "alert"].includes(c.key))
                .map((c) => renderHeaderCell(c))}
            </tr>
            <tr>
              {/* Render sub‑headers for tank and temperature columns in the current order */}
              {orderedCols
                .filter((c) => tankKeys.includes(c.key))
                .map((c) => renderHeaderCell(c))}
              {orderedCols
                .filter((c) => tempKeys.includes(c.key))
                .map((c) => renderHeaderCell(c))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const hasAlert = row.alert !== "-";
              const statusClass = String(row.status).toLowerCase().replace(/\s+/g, "-");
              return (
                <tr className="dashboard-2-clickable-row" key={row.id} onClick={() => onRowClick(row)}>
                  {orderedCols.map((col) => {
                    // Render cell based on column key
                    switch (col.key) {
                      case "name":
                        return (
                          <td key="name">
                            <strong>{row.name} <a href={`/devices/view-device/${row.id}`} target="_blank" rel="noopener noreferrer">#{row.id}</a></strong>
                            <span>{row.itemName}</span>
                          </td>
                        );
                      case "alert":
                        return (
                          <td key="alert" onClick={(event) => { event.stopPropagation(); if (hasAlert) onAlertClick(row); }}>
                            <span className={`dashboard-2-alert-cell`} style={{ backgroundColor: hasAlert ? row.badgePick.background_color_hex : "transparent", borderColor: hasAlert ? row.badgePick.border_color_hex : "transparent", color: hasAlert ? row.badgePick.font_color_hex : "inherit" }} >
                              <span className={`dashboard-2-alert-icon`} style={{ backgroundColor: hasAlert ? row.badgePick.font_color_hex : "rgba(76, 175, 80, 0.16)", color: hasAlert ? row.badgePick.background_color_hex : "#73d27a" }} >
                                {hasAlert ? "!" : "OK"}
                              </span>
                              {hasAlert ? row.alertAge : ""}
                            </span>
                          </td>
                        );
                      case "fresh":
                      case "waste":
                      case "waste2":
                      case "waste3":
                        return <ChannelCell key={col.key} channel={row[col.key]} type={col.key} hasAlert={hasAlert ? {alert: row.alert_obj, badgePick: row.badgePick} : null} />;
                      case "menTemp":
                      case "womenTemp":
                      case "utilityTemp":
                        return <TempCell key={col.key} channel={row[col.key]} hasAlert={hasAlert} />;
                      case "batteryVoltage":
                        return (
                          <td key="batteryVoltage">
                            {/* <span className={`dashboard-2-channel-value ${hasAlert ? "alert-active" : ""}`}>{row.batteryVoltage}</span> */}
                            <span className={`dashboard-2-channel-value`}>{row.batteryVoltage}</span>
                          </td>
                        );
                      case "status":
                        return (
                          <td key="status">
                            <span className={`dashboard-2-status-badge ${statusClass}`}>{row.status}</span>
                          </td>
                        );
                      case "power":
                        return (
                          <td key="power">
                            <span className={`dashboard-2-power-badge ${String(row.power).toLowerCase()}`}>{row.power}</span>
                          </td>
                        );
                      case "location":
                        return (
                          <td key="location">
                            {row.location}
                            <span>{row.entityName}</span>
                          </td>
                        );
                      case "identifier":
                        return <td key="identifier">{row.identifier}</td>;
                      case "lastUpdated":
                        return <td key="lastUpdated">{row.lastUpdated}</td>;
                      default:
                        return null;
                    }
                  })}
                </tr>
              );
            })}
            {!rows.length && (
              <tr>
                <td colSpan={columnOrder.length}>
                  <MDTypography variant="button" color="text">
                    No devices found.
                  </MDTypography>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </MDBox>
    </Card>
  );
}

export default DeviceDashboardTable;